// PCA scree plot -- variance explained by each hidden factor plus the
// cumulative curve. A dominant PC1 means one systemic bet drives everything.

import React, { useMemo } from 'react';
import {
    Bar,
    CartesianGrid,
    ComposedChart,
    Legend,
    Line,
    ResponsiveContainer,
    Tooltip,
    XAxis,
    YAxis,
} from 'recharts';

import type { PcaComponent } from '../types';

interface Props {
    components: PcaComponent[];
}

export const PcaScreeChart: React.FC<Props> = ({ components }) => {
    const data = useMemo(
        () =>
            components.map((c) => ({
                component: c.component,
                variance: c.variance_explained * 100,
                cumulative: c.cumulative_variance * 100,
            })),
        [components],
    );
    const pc1 = components[0]?.variance_explained ?? 0;

    return (
        <div className="bg-white p-5 rounded-2xl shadow-sm border border-slate-100">
            <div className="flex items-start justify-between gap-3">
                <div>
                    <h3 className="text-lg font-bold text-slate-800">Hidden Factor Variance (PCA)</h3>
                    <p className="text-xs text-slate-500 font-medium mt-1 mb-4">
                        If PC1 explains more than 60%, one systemic risk drives your portfolio.
                    </p>
                </div>
                <span
                    className={`shrink-0 text-xs font-bold px-2 py-1 rounded ${
                        pc1 > 0.6 ? 'bg-rose-50 text-rose-700' : 'bg-emerald-50 text-emerald-700'
                    }`}
                >
                    PC1 {(pc1 * 100).toFixed(0)}%
                </span>
            </div>
            <div className="h-[340px] -ml-2">
                <ResponsiveContainer width="100%" height="100%">
                    <ComposedChart data={data} margin={{ top: 10, right: 10, left: 0, bottom: 0 }}>
                        <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#e2e8f0" />
                        <XAxis dataKey="component" stroke="#64748b" tick={{ fontSize: 12 }} />
                        <YAxis
                            stroke="#64748b"
                            tick={{ fontSize: 12 }}
                            domain={[0, 100]}
                            tickFormatter={(v: number) => `${v}%`}
                            width={48}
                        />
                        <Tooltip
                            formatter={(value: number | string, name: string | number) => [
                                `${Number(value).toFixed(1)}%`,
                                name,
                            ]}
                            contentStyle={{
                                borderRadius: '8px',
                                border: 'none',
                                boxShadow: '0 4px 6px -1px rgb(0 0 0 / 0.1)',
                            }}
                        />
                        <Legend wrapperStyle={{ paddingTop: '10px', fontSize: 12 }} />
                        <Bar dataKey="variance" name="Variance Explained" fill="#10b981" radius={[4, 4, 0, 0]} maxBarSize={40} />
                        <Line
                            type="monotone"
                            dataKey="cumulative"
                            name="Cumulative"
                            stroke="#0284c7"
                            strokeWidth={2}
                            dot={{ r: 3 }}
                        />
                    </ComposedChart>
                </ResponsiveContainer>
            </div>
        </div>
    );
};
